import type { CategoryDraft, Product } from './catalog.types';

type Props = {
  editingCategory: CategoryDraft;
  products: Product[];
  categoryProductSearch: string;
  showOnlyUncategorized: boolean;
  categorySaving: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  onSubmit: (e: React.FormEvent) => void;
  setEditingCategory: (next: CategoryDraft) => void;
  setCategoryProductSearch: (value: string) => void;
  setShowOnlyUncategorized: (value: boolean) => void;
  onCancel: () => void;
};

export default function CategoryForm(props: Props) {
  const {
    editingCategory,
    products,
    categoryProductSearch,
    showOnlyUncategorized,
    categorySaving,
    canCreate,
    canUpdate,
    onSubmit,
    setEditingCategory,
    setCategoryProductSearch,
    setShowOnlyUncategorized,
    onCancel,
  } = props;

  const getFilteredCategoryProducts = () => {
    const query = categoryProductSearch.trim().toLowerCase();
    return products.filter((product) => {
      if (showOnlyUncategorized && product.category_id) return false;
      if (!query) return true;
      return product.name.toLowerCase().includes(query);
    });
  };

  return (
    <form onSubmit={onSubmit} className="admin-card admin-editor-form" style={{ marginBottom: '20px' }}>
      <label className="admin-field">
        <span className="admin-field-label">Название категории</span>
        <input
          className="admin-input"
          data-testid="admin-catalog-category-name-input"
          placeholder="Например: Букеты"
          value={editingCategory.name || ''}
          onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
          required
        />
        <span className="admin-field-hint">Название категории, которое увидит клиент в каталоге.</span>
      </label>
      <div className="admin-field">
        <span className="admin-field-label">Товары в категории</span>
        {products.length > 0 && (
          <div className="admin-category-products-toolbar">
            <input className="admin-input" placeholder="Поиск товара..." value={categoryProductSearch} onChange={(e) => setCategoryProductSearch(e.target.value)} />
            <label className="admin-category-product-row">
              <input type="checkbox" checked={showOnlyUncategorized} onChange={(e) => setShowOnlyUncategorized(e.target.checked)} />
              <span>Только без категории</span>
            </label>
          </div>
        )}
        <div className="admin-category-products-list">
          {products.length === 0 ? (
            <span className="admin-field-hint">Товаров пока нет. Можно сохранить категорию без товаров.</span>
          ) : (
            getFilteredCategoryProducts().map((product) => {
              const checked = (editingCategory.product_ids || []).includes(product.id);
              return (
                <label key={product.id} className="admin-category-product-row">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const currentIds = editingCategory.product_ids || [];
                      const nextIds = e.target.checked
                        ? Array.from(new Set([...currentIds, product.id]))
                        : currentIds.filter((id) => id !== product.id);
                      setEditingCategory({ ...editingCategory, product_ids: nextIds });
                    }}
                  />
                  <span>{product.name}</span>
                </label>
              );
            })
          )}
        </div>
        {products.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={() => {
                const visibleIds = getFilteredCategoryProducts().map((p) => p.id);
                const current = editingCategory.product_ids || [];
                const merged = Array.from(new Set([...current, ...visibleIds]));
                setEditingCategory({ ...editingCategory, product_ids: merged });
              }}
            >
              Выбрать все видимые
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={() => {
                const visibleIds = new Set(getFilteredCategoryProducts().map((p) => p.id));
                const current = editingCategory.product_ids || [];
                const next = current.filter((id) => !visibleIds.has(id));
                setEditingCategory({ ...editingCategory, product_ids: next });
              }}
            >
              Снять видимые
            </button>
            <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setEditingCategory({ ...editingCategory, product_ids: [] })}>
              Очистить выбор
            </button>
          </div>
        )}
        {(editingCategory.product_ids || []).length > 0 && (
          <div style={{ marginTop: 10 }}>
            <span className="admin-field-label">Очередность товаров в категории</span>
            <div className="admin-category-products-list">
              {(editingCategory.product_ids || []).map((productId, index) => {
                const product = products.find((p) => p.id === productId);
                if (!product) return null;
                return (
                  <div key={productId} className="admin-category-product-row" style={{ justifyContent: 'space-between' }}>
                    <span>{product.name}</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        disabled={index === 0}
                        onClick={() => {
                          const next = [...(editingCategory.product_ids || [])];
                          [next[index - 1], next[index]] = [next[index], next[index - 1]];
                          setEditingCategory({ ...editingCategory, product_ids: next });
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        disabled={index === (editingCategory.product_ids || []).length - 1}
                        onClick={() => {
                          const next = [...(editingCategory.product_ids || [])];
                          [next[index + 1], next[index]] = [next[index], next[index + 1]];
                          setEditingCategory({ ...editingCategory, product_ids: next });
                        }}
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input type="checkbox" checked={editingCategory.is_active ?? true} onChange={(e) => setEditingCategory({ ...editingCategory, is_active: e.target.checked })} />
        Активна
      </label>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={categorySaving || (editingCategory?.id ? !canUpdate : !canCreate)}>
          {categorySaving ? 'Сохранение...' : 'Сохранить'}
        </button>
        <button type="button" className="admin-btn admin-btn-secondary" disabled={categorySaving} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

